import {DateTime, DateTimeUnit} from 'luxon';
import React, {Component, createRef, RefObject} from 'react';
import styled from 'styled-components';
import {Date as SugarDate} from 'sugar';
import {Key} from 'ts-key-enum';

import {defineMessage, Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {
  Handler,
  HandlerOf,
  KeyboardEventHandler,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../srcCommon/internationalization/internationalizationContext';
import {formatDate, formatTime, TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {DatePickerContainer} from './datePickerContainer';
import {TimePicker} from './timePicker';

/*
 * Props.
 */

interface DateTimePickerProps {
  defaultTime?: DateTime;
  selectedDateTime?: DateTime;
  highlightedDateTime?: DateTime;
  minDateTime?: DateTime;
  maxDateTime?: DateTime;
  onDateTimeSelect?: HandlerOf<DateTime | undefined>;
  onDateTimeConfirm?: Handler;
  timeFormat: TimeFormatsEnum;
  isTimeFieldReadOnly?: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.foreground};
`;

const StyledDatePickerDiv = styled.div`
  position: relative;
  padding: 6px 12px 12px 12px;
`;

const StyledTimePickerDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledInputsDiv = styled.div`
  padding: 12px 12px 0 12px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};

  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  grid-column-gap: 12px;
`;

/*
 * Component.
 */

interface DateTimePickerState {
  isTimeFieldFocused: boolean;
  date?: DateTime;
  time?: DateTime;
  dateValue: string;
  timeValue: string;
}

class DateTimePickerComponent extends Component<
  DateTimePickerProps & InternationalizationContextProps,
  DateTimePickerState
> {
  constructor(props: DateTimePickerProps & InternationalizationContextProps) {
    super(props);
    const {localize, selectedDateTime, timeFormat} = props;
    this.state = {
      isTimeFieldFocused: false,
      date: selectedDateTime,
      time: selectedDateTime,
      dateValue: formatDate(localize, selectedDateTime),
      timeValue: formatTime(localize, timeFormat, selectedDateTime),
    };

    this.dateTextBoxRef = createRef<BoxedInput>();
    this.timeTextBoxRef = createRef<BoxedInput>();
  }

  private readonly dateTextBoxRef: RefObject<BoxedInput>;
  private readonly timeTextBoxRef: RefObject<BoxedInput>;

  /*
   * Lifecycle.
   */

  componentDidMount() {
    // Focus the date input after load.
    this.tryFocusDate();
  }

  /*
   * Event handlers.
   */

  private readonly onDateFocus = () => {
    this.setState({isTimeFieldFocused: false});
  };

  private readonly onTimeFocus = () => {
    this.setState({isTimeFieldFocused: true});
  };

  private readonly onDateBlur = () => {
    const {date} = this.state;
    if (!date) {
      return;
    }

    const {localize} = this.props;
    this.setState({dateValue: formatDate(localize, date)});
  };

  private readonly onTimeBlur = () => {
    const {time} = this.state;
    if (!time) {
      return;
    }

    const {localize, timeFormat} = this.props;
    this.setState({timeValue: formatTime(localize, timeFormat, time)});
  };

  private readonly onDateChange: HandlerOf<string> = (dateValue) => {
    const date = this.parseDate(dateValue);
    if (!date) {
      this.setState({date, dateValue});
      this.maybeReportDateTimeSelect(date, this.state.time);
      return;
    }

    const {time, timeValue} = this.computeTime(date);

    this.setState({date, dateValue, time, timeValue});
    this.maybeReportDateTimeSelect(date, time);
  };

  private readonly onTimeChange: HandlerOf<string> = (timeValue) => {
    const {date} = this.state;
    const time = this.parseTime(date || DateTime.local(), timeValue);
    this.setState({time, timeValue});
    this.maybeReportDateTimeSelect(date, time);
  };

  private readonly onDateSelect: HandlerOf<DateTime> = (date) => {
    const dateValue = formatDate(this.props.localize, date);

    const {time, timeValue} = this.computeTime(date);

    // Focus on the time field if one of the following:
    // - Time field is currently blank
    // - A defaultTime prop is given (the time field will rarely be blank in this case).
    //   (This is an OK experience, but OK for now as in the future we may move to always having both
    //    the date and time pickers visible, rather than switching between the two views).
    const isTimeFieldFocused = !timeValue || Boolean(this.props.defaultTime);

    this.setState({isTimeFieldFocused, date, dateValue, time, timeValue});
    this.maybeReportDateTimeSelect(date, time);

    // If we're moving to the time picker, focus accordingly.
    if (isTimeFieldFocused) {
      this.tryFocusTime();
    }
  };

  private readonly onTimeSelect: HandlerOf<DateTime> = (time) => {
    const {localize, timeFormat} = this.props;
    const timeValue = formatTime(localize, timeFormat, time);
    this.setState({time, timeValue});
    this.maybeReportDateTimeSelect(this.state.date, time);
  };

  private readonly onDateKeyDown: KeyboardEventHandler = (event) => {
    if (event.key !== Key.Enter) {
      return;
    }

    // We need a valid date.
    if (!this.state.date) {
      return;
    }

    // Notify our parent.
    const {onDateTimeConfirm} = this.props;
    if (onDateTimeConfirm) {
      onDateTimeConfirm();
    }

    // Focus the time input.
    this.tryFocusTime();
  };

  private readonly onTimeKeyDown: KeyboardEventHandler = (event) => {
    if (event.key !== Key.Enter) {
      return;
    }

    // We need a valid time.
    if (!this.state.time) {
      return;
    }

    // Notify our parent.
    const {onDateTimeConfirm} = this.props;
    if (onDateTimeConfirm) {
      onDateTimeConfirm();
    }

    // Focus the date input.
    this.tryFocusDate();
  };

  /*
   * Helpers.
   */

  private tryFocusDate() {
    const currentDateTextBox = this.dateTextBoxRef.current;
    if (!currentDateTextBox) {
      return;
    }

    currentDateTextBox.focus();
  }

  private tryFocusTime() {
    const currentTimeTextBox = this.timeTextBoxRef.current;
    if (!currentTimeTextBox) {
      return;
    }

    currentTimeTextBox.focus();
  }

  private maybeReportDateTimeSelect(date?: DateTime, time?: DateTime) {
    // We may not need to report anything.
    const {onDateTimeSelect} = this.props;
    if (!onDateTimeSelect) {
      return;
    }

    // If we miss either date or time, report undefined.
    if (!date || !time) {
      onDateTimeSelect(undefined);
      return;
    }

    // Otherwise, combine the datetimes.
    const dateTime = mergeDateAndTime(date, time);
    onDateTimeSelect(dateTime);
  }

  private parseDate(dateValue: string) {
    const {i18n, minDateTime, maxDateTime} = this.props;

    // Parse the date.
    const date = DateTime.fromJSDate(SugarDate.create(dateValue, {locale: i18n.locale}));
    if (!date.isValid) {
      return undefined;
    }

    // Check boundaries.
    const isAfterMinDateTime = !minDateTime || minDateTime < date.endOf('day');
    const isBeforeMinDateTime = !maxDateTime || maxDateTime > date.startOf('day');
    if (!isAfterMinDateTime || !isBeforeMinDateTime) {
      return undefined;
    }

    return date;
  }

  private parseTime(date: DateTime, timeValue: string) {
    const {i18n, minDateTime, maxDateTime} = this.props;

    // Parse the date.
    const parsedTime = DateTime.fromJSDate(SugarDate.create(timeValue, {locale: i18n.locale}));
    if (!parsedTime.isValid) {
      return undefined;
    }

    // Check boundaries.
    const time = mergeDateAndTime(date, parsedTime);
    const isAfterMinDateTime = !minDateTime || minDateTime < time.endOf('minute');
    const isBeforeMinDateTime = !maxDateTime || maxDateTime > time.startOf('minute');
    if (!isAfterMinDateTime || !isBeforeMinDateTime) {
      return undefined;
    }

    return time;
  }

  private computeTime(date: DateTime): {time?: DateTime; timeValue: string} {
    const newTime = clampTime(this.props, 'minute', date, this.state.time);
    const newTimeValue = formatTime(this.props.localize, this.props.timeFormat, newTime);

    return {time: newTime, timeValue: newTimeValue};
  }

  /*
   * Renders.
   */

  render() {
    const {i18n, highlightedDateTime, minDateTime, maxDateTime, isTimeFieldReadOnly} = this.props;
    const {date, dateValue, timeValue} = this.state;
    return (
      <StyledWrapperDiv>
        <StyledDatePickerDiv>
          <DatePickerContainer
            selectedDate={date}
            highlightedDate={highlightedDateTime}
            minDate={minDateTime}
            maxDate={maxDateTime}
            onDateSelect={this.onDateSelect}
          />
          {this.maybeRenderTimePicker()}
        </StyledDatePickerDiv>
        <StyledInputsDiv>
          <TextBox
            ref={this.dateTextBoxRef}
            label={<Trans id="datetime-picker-date">Date</Trans>}
            placeholder={i18n._(
              defineMessage({
                id: 'datetime-picker-date-placeholder',
                message: 'mm/dd/yyyy',
              }),
            )}
            onChange={this.onDateChange}
            onFocus={this.onDateFocus}
            onBlur={this.onDateBlur}
            onKeyDown={this.onDateKeyDown}
            value={dateValue}
          />
          <TextBox
            ref={this.timeTextBoxRef}
            label={<Trans id="datetime-picker-time">Time</Trans>}
            placeholder={i18n._(
              defineMessage({
                id: 'datetime-picker-time-placeholder',
                message: 'hh:mm',
              }),
            )}
            onChange={this.onTimeChange}
            onFocus={this.onTimeFocus}
            onBlur={this.onTimeBlur}
            onKeyDown={this.onTimeKeyDown}
            value={timeValue}
            isReadOnly={isTimeFieldReadOnly}
          />
        </StyledInputsDiv>
      </StyledWrapperDiv>
    );
  }

  private maybeRenderTimePicker() {
    // Render the time picker on top of the date picker if requested.
    const {isTimeFieldFocused, date, time} = this.state;
    if (!isTimeFieldFocused) {
      return null;
    }

    const {minDateTime, maxDateTime, timeFormat} = this.props;
    return (
      <StyledTimePickerDiv>
        <TimePicker
          startDay={date}
          selectedTime={time}
          minTime={minDateTime}
          maxTime={maxDateTime}
          onTimeSelect={this.onTimeSelect}
          timeFormat={timeFormat}
        />
      </StyledTimePickerDiv>
    );
  }
}

export const DateTimePicker = withInternationalization(DateTimePickerComponent);

/*
 * Helpers.
 */

/**
 * @returns True only if the defaultTime on the given date is within the range of the minDateTime and
 * maxDateTime props. Either limit is valid if the corresponding prop is undefined.
 */
function isDefaultTimeWithinRange(
  unit: DateTimeUnit,
  date: DateTime,
  defaultTime: DateTime,
  minDateTime: DateTimePickerProps['minDateTime'],
  maxDateTime: DateTimePickerProps['maxDateTime'],
): boolean {
  const dateAtDefaultTime = date.set({hour: defaultTime.hour, minute: defaultTime.minute});

  const isAfterMin = !minDateTime || minDateTime < dateAtDefaultTime.endOf(unit);
  const isBeforeMax = !maxDateTime || maxDateTime > dateAtDefaultTime.startOf(unit);

  return isAfterMin && isBeforeMax;
}

/**
 * @param props The props given to the component
 * @param unit Relevant unit for clamping within bounds
 * @param date Chosen date (time portion is not relevant)
 * @param time Chosen time (date portion is not relevant) (optional)
 *
 * @returns A DateTime instance (that represents the time) or undefined.
 * The given time is clamped within the bounds of the minDateTime and maxDateTime props.
 * If the time is not given, the defaultTime prop exists, and the defaultTime on the given date are inside the
 * bounds, it is used.
 */
function clampTime(
  props: DateTimePickerProps,
  unit: DateTimeUnit,
  date: DateTime,
  time?: DateTime,
): DateTime | undefined {
  const {maxDateTime, minDateTime, defaultTime} = props;

  // Use defaultTime if it is in range for the given date and a time hasn't already been chosen:
  if (!time && defaultTime && isDefaultTimeWithinRange(unit, date, defaultTime, minDateTime, maxDateTime)) {
    return defaultTime;
  }

  if (!time) {
    return undefined;
  }

  const dateAndTime = mergeDateAndTime(date, time);

  if (minDateTime && minDateTime > dateAndTime.endOf(unit)) {
    return minDateTime;
  }

  if (maxDateTime && maxDateTime < dateAndTime.startOf(unit)) {
    return maxDateTime;
  }

  return dateAndTime;
}

/**
 * @param date Chosen date (time portion is not relevant)
 * @param time Chosen time (date portion is not relevant)
 */
function mergeDateAndTime(date: DateTime, time: DateTime): DateTime {
  return date.startOf('minute').set({
    hour: time.hour,
    minute: time.minute,
  });
}
